import React from 'react';
import { useTheme, getAvatarForUser, RANK_BADGES } from '../context/ThemeContext';

// Player Avatar Component
export const PlayerAvatar = ({ username, size = 'md', showName = false, className = '' }) => {
    const { isFunMode } = useTheme();
    const avatar = getAvatarForUser(username);
    
    const sizeClasses = {
        sm: 'w-6 h-6 text-sm',
        md: 'w-8 h-8 text-lg',
        lg: 'w-12 h-12 text-2xl',
        xl: 'w-16 h-16 text-3xl'
    };

    if (!isFunMode) return null;

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <div 
                className={`${sizeClasses[size]} rounded-full flex items-center justify-center fun-avatar-bounce`}
                style={{ 
                    backgroundColor: `${avatar.color}20`,
                    boxShadow: `0 0 15px ${avatar.color}50`
                }}
            >
                <span className="fun-avatar-emoji">{avatar.emoji}</span>
            </div>
            {showName && (
                <span className="font-bold text-white" style={{ textShadow: `0 0 10px ${avatar.color}` }}>
                    {username?.toUpperCase()}
                </span>
            )}
        </div>
    );
};

// Rank Badge Component with Gaming Style
export const RankBadge = ({ rank, size = 'md', showTitle = false }) => {
    const { isFunMode } = useTheme();
    const badge = RANK_BADGES[rank] || RANK_BADGES.default;
    
    const sizeClasses = {
        sm: 'w-6 h-6 text-sm',
        md: 'w-8 h-8 text-lg',
        lg: 'w-10 h-10 text-xl'
    };

    // Normal mode - simple number badge
    if (!isFunMode) {
        return (
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm ${
                rank === 1 ? 'bg-yellow-500/20 text-yellow-500' :
                rank === 2 ? 'bg-zinc-400/20 text-zinc-400' :
                rank === 3 ? 'bg-orange-500/20 text-orange-500' :
                'bg-zinc-800 text-zinc-500'
            }`}>
                {rank}
            </span>
        );
    }

    // Fun mode - gaming style badge
    return (
        <div className="flex items-center gap-1">
            <div 
                className={`${sizeClasses[size]} rounded-full flex items-center justify-center fun-rank-pulse`}
                style={{ 
                    boxShadow: `0 0 20px ${badge.glow}80, inset 0 0 10px ${badge.glow}40`,
                    background: `radial-gradient(circle at 30% 30%, ${badge.glow}40, transparent)`
                }}
            >
                <span className="fun-badge-emoji">{badge.emoji}</span>
            </div>
            {showTitle && rank <= 3 && (
                <span 
                    className="text-xs font-bold fun-title-glow"
                    style={{ color: badge.glow, textShadow: `0 0 10px ${badge.glow}` }}
                >
                    {badge.title}
                </span>
            )}
        </div>
    );
};

// Score Display with Gaming Effects
export const FunScore = ({ score, size = 'md' }) => {
    const { isFunMode } = useTheme();
    
    const isPositive = score > 0;
    const isNegative = score < 0;
    const color = isPositive ? '#39FF14' : isNegative ? '#FF3131' : '#888';
    
    const sizeClasses = {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-xl',
        xl: 'text-2xl'
    };

    if (!isFunMode) {
        return (
            <span className={`font-mono font-semibold ${sizeClasses[size]} ${
                isPositive ? 'text-neon-green' : isNegative ? 'text-red-500' : 'text-zinc-400'
            }`}>
                {isPositive ? '+' : ''}{score?.toLocaleString()}
            </span>
        );
    }

    return (
        <span 
            className={`font-mono font-bold ${sizeClasses[size]} fun-score-glow`}
            style={{ 
                color: color,
                textShadow: `0 0 10px ${color}, 0 0 20px ${color}50`,
                animation: isPositive ? 'scoreUp 0.5s ease-out' : isNegative ? 'scoreDown 0.5s ease-out' : 'none'
            }}
        >
            {isPositive && <span className="fun-plus-icon">▲</span>}
            {isNegative && <span className="fun-minus-icon">▼</span>}
            {' '}{Math.abs(score)?.toLocaleString()}
            {isPositive && <span className="fun-sparkle">✨</span>}
        </span>
    );
};

// Leaderboard Row with Full Gaming Style
export const FunLeaderboardRow = ({ entry, rank, isCurrentUser = false }) => {
    const { isFunMode } = useTheme();
    const avatar = getAvatarForUser(entry.username);
    
    if (!isFunMode) {
        return null; // Use default rendering
    }

    return (
        <tr 
            className={`fun-row-hover ${isCurrentUser ? 'fun-current-user' : ''} ${rank <= 3 ? 'fun-top-three' : ''}`}
            style={{
                '--row-glow': avatar.color,
                animation: `rowAppear 0.3s ease-out ${rank * 0.1}s both`
            }}
        >
            <td className="py-3">
                <RankBadge rank={rank} showTitle={rank <= 3} />
            </td>
            <td className="py-3">
                <div className="flex items-center gap-3">
                    <PlayerAvatar username={entry.username} size="md" />
                    <div className="flex flex-col">
                        <span 
                            className="font-bold text-white uppercase"
                            style={{ textShadow: `0 0 10px ${avatar.color}` }}
                        >
                            {entry.username?.toUpperCase()}
                            {isCurrentUser && (
                                <span className="ml-2 text-xs bg-neon-green/20 text-neon-green px-2 py-0.5 rounded-full fun-you-badge">
                                    YOU
                                </span>
                            )}
                        </span>
                        {rank === 1 && (
                            <span className="text-xs text-yellow-400 fun-champion-text">
                                🔥 On Fire!
                            </span>
                        )}
                    </div>
                </div>
            </td>
            <td className="py-3">
                <FunScore score={entry.total_score} size="md" />
            </td>
            <td className="py-3 text-zinc-400">
                <span className="flex items-center gap-1">
                    🎯 {entry.score_count}
                </span>
            </td>
        </tr>
    );
};

// Fun Mode Toggle Button
export const FunModeToggle = ({ className = '' }) => {
    const { isFunMode, toggleFunMode } = useTheme();
    
    return (
        <button
            onClick={toggleFunMode}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${
                isFunMode 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/50' 
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            } ${className}`}
            title={isFunMode ? 'Switch to Normal Mode' : 'Switch to Fun Mode'}
        >
            <span className={`text-lg ${isFunMode ? 'fun-emoji-spin' : ''}`}>
                {isFunMode ? '🎮' : '💼'}
            </span>
            <span className="text-sm font-medium">
                {isFunMode ? 'Fun Mode' : 'Normal'}
            </span>
        </button>
    );
};

// Confetti Effect for Winners
export const WinnerConfetti = ({ show }) => {
    const { isFunMode } = useTheme();
    
    if (!isFunMode || !show) return null;
    
    return (
        <div className="fun-confetti-container">
            {[...Array(20)].map((_, i) => (
                <div 
                    key={i}
                    className="fun-confetti-piece"
                    style={{
                        '--delay': `${Math.random() * 2}s`,
                        '--x': `${Math.random() * 100}%`,
                        '--rotation': `${Math.random() * 360}deg`,
                        backgroundColor: ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#C77DFF'][i % 5]
                    }}
                />
            ))}
        </div>
    );
};
